/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import {
  APPLICATIONS_BASE_URL,
  BENEFIT_BASE_URL,
  MVP_PROFILE_BASE_URL,
  MVP_PROFILE_BLOCK_SHIPPING_BASE_URL,
  MVP_PROGRAM_PROFILE_BASE_URL,
  MVP_RETIRE_BASE_URL,
} from "Constants/SagaConstants";
import { TenantTypesEnum } from "Constants/TenantTypes";
import { IClaimBenefitDataResult } from "Interfaces/Benefits/IClaimBenefitDataResult";
import { IExtendAzureBenefitCreditDataResult } from "Interfaces/Benefits/IExtendAzureBenefitCreditDataResult";
import { IM365BenefitDetail } from "Interfaces/Benefits/IM365BenefitDetail";
import { IM365BenefitDetailDataResult } from "Interfaces/Benefits/IM365BenefitDetailDataResult";
import { IMicrosoft365BenefitDetail } from "Interfaces/Benefits/IMicrosoft365BenefitDetail";
import { IMicrosoft365BenefitDetailDataResult } from "Interfaces/Benefits/IMicrosoft365BenefitDetailDataResult";
import { IProfileBenefitDataResult } from "Interfaces/Benefits/IProfileBenefitDataResult";
import { ICommentAddRequest } from "Interfaces/Comments/ICommentAddRequest";
import { ICommentAddResult } from "Interfaces/Comments/ICommentAddResult";
import { ICommentsGetResult } from "Interfaces/Comments/ICommentsGetResult";
import { ICapDataResult } from "Interfaces/Demographics/ICapDataResult";
import { IDemographicsRequest } from "Interfaces/Demographics/IDemographicsRequest";
import { IDemographicsResult } from "Interfaces/Demographics/IDemographicsResult";
import { IApiEmptyResult, IApiResult2 } from "Interfaces/IApiResult";
import { IMVPAzureBenefitClaim } from "Interfaces/MVP/IMVPAzureBenefitClaim";
import { IMVPProgramProfile } from "Interfaces/MVP/IMVPProgramProfile";
import { IMVPProgramProfileUpdateRequest } from "Interfaces/MVP/IMVPProgramProfileUpdateRequest";
import { IMVPRetireProfileRequest } from "Interfaces/MVP/IMVPRetireProfileRequest";
import { IMVPRetireProfileResponse } from "Interfaces/MVP/IMVPRetireProfileResponse";
import { IMVPUserProfileGetResult } from "Interfaces/MVP/IMVPUserProfileGetResult";
import { IMVPUserProfileSearchRequest } from "Interfaces/MVP/IMVPUserProfileSearchRequest";
import { IMVPUserProfileSearchResult } from "Interfaces/MVP/IMVPUserProfileSearchResult";
import { IUserProfileBlockShippingDataResult } from "Interfaces/UserProfile/IUserProfileBlockShippingDataResult";
import { IUserProfileBlockShippingRequest } from "Interfaces/UserProfile/IUserProfileBlockShippingRequest";
import { IUserProfileDataResult } from "Interfaces/UserProfile/IUserProfileDataResult";
import { get, post, put, IHttpResponse } from "Services/ApiUtils";

/**
 * Fetch MVP user profile comments.
 * @param {number} mvpUserProfileId The user profile id to fetch.
 */
export async function fetchUserComments(
  mvpUserProfileId: number
): Promise<IHttpResponse<IApiResult2<ICommentsGetResult>>> {
  return await get<IApiResult2<ICommentsGetResult>>(
    MVP_PROFILE_BASE_URL + mvpUserProfileId + "/Comments",
    true
  );
}

/**
 * Add MVP user profile comment.
 * @param {ICommentAddRequest} request The request.
 */
export async function addNewComment(
  request: ICommentAddRequest
): Promise<IHttpResponse<IApiResult2<ICommentAddResult>>> {
  return await post<IApiResult2<ICommentAddResult>>(
    MVP_PROFILE_BASE_URL + request.userProfileComment.userProfileId + "/Comments",
    request,
    true
  );
}

/**
 * Fetch MVP user profile.
 * @param {ICommentAddRequest} request The request.
 */
export async function fetchUserProfile(
  mvpUserProfileId: number
): Promise<IHttpResponse<IApiResult2<IMVPUserProfileGetResult>>> {
  return await get<IApiResult2<IMVPUserProfileGetResult>>(
    MVP_PROFILE_BASE_URL + mvpUserProfileId,
    true
  );
}

/**
 * Claim the code for the Benefit Manager associated with the user profile.
 * @param {string} banefitFileId The benefit file id.
 * @param {number} userProfileId The profile id.
 * @returns {IClaimBenefitDataResult}
 */
export async function claimUserProfileBenefitManager(
  banefitFileId: string,
  userProfileId: number
): Promise<IHttpResponse<IApiResult2<IClaimBenefitDataResult>>> {
  return await post<IApiResult2<IClaimBenefitDataResult>>(
    `${BENEFIT_BASE_URL.replace("{tenant}", TenantTypesEnum.MVP.toLowerCase())}${banefitFileId}`,
    { userProfileId: userProfileId },
    true
  );
}

/**
 * Claims the github pro benefit for the user profile.
 * @param {number} userProfileId The profile id.
 * @returns {IClaimBenefitDataResult}
 */
export async function claimUserProfileBenefitsGithubPro(
  userProfileId: number
): Promise<IHttpResponse<IApiResult2<IClaimBenefitDataResult>>> {
  return await post<IApiResult2<IClaimBenefitDataResult>>(
    `${BENEFIT_BASE_URL.replace("{tenant}", TenantTypesEnum.MVP.toLowerCase())}github`,
    { userProfileId: userProfileId },
    true
  );
}

/**
 * Claims the MCP benefit for the user profile.
 * @param {number} userProfileId The profile id.
 * @returns {IClaimBenefitDataResult}
 */
export async function claimUserProfileBenefitsMcp(
  userProfileId: number
): Promise<IHttpResponse<IApiResult2<IClaimBenefitDataResult>>> {
  return await post<IApiResult2<IClaimBenefitDataResult>>(
    `${BENEFIT_BASE_URL.replace("{tenant}", TenantTypesEnum.MVP.toLowerCase())}mcp`,
    { userProfileId: userProfileId },
    true
  );
}

/**
 * Claims the LinkedIn benefit for the user profile.
 * @param {number} userProfileId The profile id.
 */
export async function claimUserProfileBenefitsLinkedIn(
  userProfileId: number
): Promise<IHttpResponse<IApiResult2<IClaimBenefitDataResult>>> {
  return await post<IApiResult2<IClaimBenefitDataResult>>(
    `${BENEFIT_BASE_URL.replace("{tenant}", TenantTypesEnum.MVP.toLowerCase())}linkedin`,
    { userProfileId: userProfileId },
    true
  );
}

/**
 * Claims the M365 benefit for the user profile.
 * @param {number} userProfileId The profile id.
 */
export async function claimUserProfileBenefitsM365(
  userProfileId: number
): Promise<IHttpResponse<IApiResult2<IClaimBenefitDataResult>>> {
  return await post<IApiResult2<IClaimBenefitDataResult>>(
    `${BENEFIT_BASE_URL.replace("{tenant}", TenantTypesEnum.MVP.toLowerCase())}M365`,
    { userProfileId: userProfileId },
    true
  );
}

/**
 * Claims the MS365 benefit for the user profile.
 * @param {number} userProfileId The profile id.
 */
export async function claimUserProfileBenefitsMS365(
  userProfileId: number
): Promise<IHttpResponse<IApiResult2<IClaimBenefitDataResult>>> {
  return await post<IApiResult2<IClaimBenefitDataResult>>(
    `${BENEFIT_BASE_URL.replace("{tenant}", TenantTypesEnum.MVP.toLowerCase())}MS365`,
    { userProfileId: userProfileId },
    true
  );
}

/**
 * Claims the Teams benefit for the user profile.
 * @param {number} userProfileId The profile id.
 */
export async function claimUserProfileBenefitsTeams(
  userProfileId: number
): Promise<IHttpResponse<IApiResult2<IClaimBenefitDataResult>>> {
  return await post<IApiResult2<IClaimBenefitDataResult>>(
    `${BENEFIT_BASE_URL.replace("{tenant}", TenantTypesEnum.MVP.toLowerCase())}teams`,
    { userProfileId: userProfileId },
    true
  );
}

/**
 * Claims the Award Kits benefit for the user profile.
 * @param {number} userProfileId The profile id.
 */
export async function claimUserProfileBenefitsAwardKits(
  userProfileId: number,
  SkuId: string
): Promise<IHttpResponse<IApiResult2<IClaimBenefitDataResult>>> {
  return await post<IApiResult2<IClaimBenefitDataResult>>(
    `${BENEFIT_BASE_URL.replace("{tenant}", TenantTypesEnum.MVP.toLowerCase())}awardkit`,
    { userProfileId: userProfileId, SkuId: SkuId },
    true
  );
}

/**
 * Fetch MVP program profile.
 * @param {IMVPProgramProfileGetResult} request The request.
 */
export async function fetchProgramProfile(
  mvpUserProfileId: number
): Promise<IHttpResponse<IApiResult2<IMVPProgramProfile>>> {
  return await get<IApiResult2<IMVPProgramProfile>>(
    MVP_PROGRAM_PROFILE_BASE_URL + mvpUserProfileId,
    true
  );
}

/**
 * Update MVP program profile.
 * @param {IMVPProgramProfileUpdateRequest} request The request.
 */
export async function updateProgramProfile(
  request: IMVPProgramProfileUpdateRequest
): Promise<IHttpResponse<IApiResult2<boolean>>> {
  return await put<IApiResult2<boolean>>(MVP_PROGRAM_PROFILE_BASE_URL, request, true);
}

/**
 * Fetch retire profile.
 * @param {IMVPRetireProfileResponse} request The request.
 */
export async function fetchRetireProfile(
  userProfileId: number
): Promise<IHttpResponse<IApiResult2<IMVPRetireProfileResponse>>> {
  return await get<IApiResult2<IMVPRetireProfileResponse>>(
    MVP_RETIRE_BASE_URL + userProfileId,
    true
  );
}

/**
 * Update MVP program profile.
 * @param {IMVPProgramProfileUpdateRequest} request The request.
 */
export async function retireProfile(
  request: IMVPRetireProfileRequest
): Promise<IHttpResponse<IApiResult2<boolean>>> {
  return await put<IApiResult2<boolean>>(MVP_RETIRE_BASE_URL, request, true);
}

/**
 * Update the user profile block shipping field.
 * @param userProfile - The request object.
 */
export async function updateUserProfileBlockShipping(
  blockShippingRequest: IUserProfileBlockShippingRequest
): Promise<IHttpResponse<IApiResult2<IUserProfileBlockShippingDataResult>>> {
  return await put<IApiResult2<IUserProfileBlockShippingDataResult>>(
    MVP_PROFILE_BLOCK_SHIPPING_BASE_URL,
    blockShippingRequest,
    true
  );
}

/**
 * Gets the benefits for the user profile.
 * @param {number} userProfileId The profile id.
 * @returns {IProfileBenefitDataResult}
 */
export async function getProfileBenefits(
  userProfileId: number
): Promise<IHttpResponse<IApiResult2<IProfileBenefitDataResult>>> {
  return await get<IApiResult2<IProfileBenefitDataResult>>(
    `${BENEFIT_BASE_URL.replace("{tenant}", TenantTypesEnum.MVP.toLowerCase())}${userProfileId}`,
    true
  );
}

/**
 * Search MVP user profiles.
 * @param searchRequest {IMVPUserProfileSearchRequest}
 * @returns {Promise<IHttpResponse<IApiResult2<IMVPUserProfileSearchResult>>>}
 */
export async function searchUserProfiles(
  searchRequest: IMVPUserProfileSearchRequest
): Promise<IHttpResponse<IApiResult2<IMVPUserProfileSearchResult>>> {
  return await post<IApiResult2<IMVPUserProfileSearchResult>>(
    MVP_PROFILE_BASE_URL + "search",
    searchRequest,
    true
  );
}

/**
 * Gets a users M365 Benefit Details.
 * @param userProfileId the userprofile id to fetch the benefit details for.
 * @returns {IM365BenefitDetailDataResult}
 */
export async function getM365BenefitDetails(
  userProfileId: number
): Promise<IHttpResponse<IApiResult2<IM365BenefitDetailDataResult>>> {
  return await get<IApiResult2<IM365BenefitDetailDataResult>>(
    `${BENEFIT_BASE_URL.replace(
      "{tenant}",
      TenantTypesEnum.MVP.toLowerCase()
    )}m365detail/${userProfileId}`,
    true
  );
}

/**
 * Updates the users M365 Benefit Details that helps with renewing the M365 Benefit.
 * @param m365BenefitDetails the m365 subscription details to update/add.
 * @returns {IM365BenefitDetailDataResult}
 */
export async function updateM365BenefitDetails(
  m365BenefitDetails: IM365BenefitDetail
): Promise<IHttpResponse<IApiResult2<IM365BenefitDetailDataResult>>> {
  return await put<IApiResult2<IM365BenefitDetailDataResult>>(
    `${BENEFIT_BASE_URL.replace("{tenant}", TenantTypesEnum.MVP.toLowerCase())}m365Detail`,
    m365BenefitDetails,
    true
  );
}

/**
 * Gets a users Microsoft 365 Benefit Details.
 * @param userProfileId the userprofile id to fetch the benefit details for.
 * @returns {IMicrosoft365BenefitDetailDataResult}
 */
export async function getMS365BenefitDetails(
  userProfileId: number
): Promise<IHttpResponse<IApiResult2<IMicrosoft365BenefitDetailDataResult>>> {
  return await get<IApiResult2<IMicrosoft365BenefitDetailDataResult>>(
    `${BENEFIT_BASE_URL.replace(
      "{tenant}",
      TenantTypesEnum.MVP.toLowerCase()
    )}ms365detail/${userProfileId}`,
    true
  );
}

/**
 * Updates the users Microsoft 365 Benefit Details that helps with renewing the MS365 Benefit.
 * @param ms365BenefitDetails the ms365 subscription details to update/add.
 * @returns {IMicrosoft365BenefitDetailDataResult}
 */
export async function updateMS365BenefitDetails(
  m365BenefitDetails: IMicrosoft365BenefitDetail
): Promise<IHttpResponse<IApiResult2<IMicrosoft365BenefitDetailDataResult>>> {
  return await put<IApiResult2<IMicrosoft365BenefitDetailDataResult>>(
    `${BENEFIT_BASE_URL.replace("{tenant}", TenantTypesEnum.MVP.toLowerCase())}ms365detail`,
    m365BenefitDetails,
    true
  );
}

export async function extendAzureBenefitCredit(
  credit: number,
  userProfileId: number
): Promise<IHttpResponse<IApiResult2<IExtendAzureBenefitCreditDataResult>>> {
  return await put<IApiResult2<IExtendAzureBenefitCreditDataResult>>(
    `${BENEFIT_BASE_URL.replace("{tenant}", TenantTypesEnum.MVP.toLowerCase())}azure/extend`,
    { credit, userProfileId },
    true
  );
}

/**
 * Submits an Azure benefit claim for a user.
 * @param request The details of the Azure benefit claim.
 * @param userProfileId The ID of the user profile making the claim.
 */
export async function saveAzureBenefitClaim(
  request: IMVPAzureBenefitClaim,
  userProfileId: number
): Promise<IHttpResponse<IApiResult2<boolean>>> {
  return await post<IApiResult2<boolean>>(
    `${BENEFIT_BASE_URL.replace("{tenant}", TenantTypesEnum.MVP.toLowerCase())}azure/azureBenefit`,
    { ...request, userProfileId },
    true
  );
}

/**
 * Updates the user profile user flag.
 * @param userProfileId The user profile id.
 * @param isPrivate The user profile flag.
 */
export async function updateUserProfileIsPrivate(
  userProfileId: number,
  isPrivate: boolean
): Promise<IHttpResponse<IApiResult2<IApiEmptyResult>>> {
  return await post<IApiResult2<IApiEmptyResult>>(
    `${MVP_PROFILE_BASE_URL}MarkPrivate`,
    { userProfileId, isPrivate },
    true
  );
}

/**
 * Updates the user profile is Annual Exception.
 * @param userProfileId The user profile id.
 * @param justification The justification with annual exception.
 */
export async function updateUserProfileAnnualException(
  userProfileId: number,
  justification: string
): Promise<IHttpResponse<IApiResult2<IApiEmptyResult>>> {
  return await post<IApiResult2<IApiEmptyResult>>(
    `${MVP_PROFILE_BASE_URL}AnnualException`,
    { userProfileId, justification },
    true
  );
}

/**
 * Gets MVP demographic data, filtered.
 */
export async function getFilteredDemographicsData(
  request: IDemographicsRequest
): Promise<IHttpResponse<IApiResult2<IDemographicsResult>>> {
  return await post<IApiResult2<IDemographicsResult>>(
    APPLICATIONS_BASE_URL.replace("{tenant}", TenantTypesEnum.MVP) + "demographics",
    request,
    true
  );
}

/**
 * Gets MVP cap data.
 */
export async function getCapacityData(): Promise<IHttpResponse<IApiResult2<ICapDataResult>>> {
  return await get<IApiResult2<ICapDataResult>>(
    APPLICATIONS_BASE_URL.replace("{tenant}", TenantTypesEnum.MVP) + "capacity",
    true
  );
}

/**
 * Updates the user profile primary email.
 * @param userProfileId The user profile id.
 * @param newEmail The new email.
 * @param requestorEmail The requestor email.
 */
export async function updateUserProfilePrimaryEmail(
  userProfileId: number,
  newEmail: string
): Promise<IHttpResponse<IApiResult2<IUserProfileDataResult>>> {
  return await put<IApiResult2<IUserProfileDataResult>>(
    `${MVP_PROFILE_BASE_URL}UpdatePrimaryEmail`,
    { userProfileId, newEmail },
    true
  );
}
